/**
 * Padhai EMI - Educational Loan Page
 * Integrated with Fee Structure for auto-populated amounts
 * Shows different view based on NBFC approval status
 * 
 * NBFC Approved School: Full features for parents
 * Unapproved School: Demo mode only for school admin
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fee Structure Based Loan Calculator
const FeeBasedLoanCalculator = ({ schoolId, token, isApproved }) => {
  const [selectedClass, setSelectedClass] = useState('Class 1');
  const [loanData, setLoanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTenure, setSelectedTenure] = useState(6);
  
  const classes = ['Nursery', 'LKG', 'UKG', ...Array.from({length: 12}, (_, i) => `Class ${i + 1}`)];

  const loadLoanData = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/padhai-emi/school/${schoolId}/loan-eligible/${selectedClass}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      const data = await res.json();
      if (data.success) {
        setLoanData(data);
      }
    } catch (err) {
      console.error('Error loading loan data:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLoanData();
  }, [selectedClass, schoolId]);

  const selectedEMI = loanData?.emi_options?.find(e => e.tenure_months === selectedTenure);

  return (
    <div className="space-y-6">
      {/* Class Selection */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <label className="block text-sm font-medium mb-2">Select Class</label>
        <div className="flex gap-2 flex-wrap">
          {classes.slice(0, 12).map(cls => (
            <button
              key={cls}
              onClick={() => setSelectedClass(cls)}
              className={`px-3 py-2 rounded-lg text-sm transition ${
                selectedClass === cls 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {cls}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : loanData?.has_fee_structure === false ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <span className="text-4xl">📋</span>
          <h3 className="font-bold text-red-700 mt-2">Fee Structure Not Found</h3>
          <p className="text-red-600 text-sm mt-1">
            Please setup Fee Structure first to calculate loan amount
          </p>
          <a href="/fee-structure" className="inline-block mt-4 px-4 py-2 bg-red-500 text-white rounded-lg">
            Setup Fee Structure →
          </a>
        </div>
      ) : loanData ? (
        <>
          {/* Loan Amount Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
              <p className="text-sm text-green-600">NBFC Eligible Loan</p>
              <p className="text-3xl font-bold text-green-700">
                ₹{loanData.loan_calculation?.eligible_amount?.toLocaleString()}
              </p>
              <p className="text-xs text-green-500">{loanData.loan_calculation?.coverage_percent}% of total fee</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-center">
              <p className="text-sm text-gray-600">Not Covered</p>
              <p className="text-2xl font-bold text-gray-700">
                ₹{loanData.loan_calculation?.excluded_amount?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">Pay directly to school</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200 text-center">
              <p className="text-sm text-orange-600">Total Fee</p>
              <p className="text-2xl font-bold text-orange-700">
                ₹{loanData.loan_calculation?.total_fee?.toLocaleString()}
              </p>
              <p className="text-xs text-orange-500">{selectedClass}</p>
            </div>
          </div>

          {/* EMI Options */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="font-bold text-lg mb-4">📅 EMI Options</h3>
            <div className="grid grid-cols-4 gap-3">
              {loanData.emi_options?.map(option => (
                <button
                  key={option.tenure_months}
                  onClick={() => setSelectedTenure(option.tenure_months)}
                  className={`p-4 rounded-xl border-2 transition ${
                    selectedTenure === option.tenure_months
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <p className="font-bold text-lg">{option.tenure_months} महीने</p>
                  <p className="text-2xl font-bold text-orange-600">
                    ₹{option.monthly_emi?.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">/month @ {option.interest_rate}%</p>
                </button>
              ))}
            </div>

            {selectedEMI && (
              <div className="mt-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 border border-orange-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Loan Amount</p>
                    <p className="font-bold text-lg">₹{loanData.loan_calculation?.eligible_amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Monthly EMI</p>
                    <p className="font-bold text-lg text-orange-600">₹{selectedEMI.monthly_emi?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Payable</p>
                    <p className="font-bold text-lg">₹{selectedEMI.total_payable?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fee Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            {/* Eligible Heads */}
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                ✅ Loan में शामिल ({loanData.eligible_heads?.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {loanData.eligible_heads?.map((head, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-sm">{head.head_name_hi || head.head_name}</span>
                    <span className="font-medium text-green-600">₹{head.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Excluded Heads */}
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                ❌ Loan में शामिल नहीं ({loanData.excluded_heads?.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {loanData.excluded_heads?.map((head, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 border-b border-gray-100">
                    <span className="text-sm">{head.head_name_hi || head.head_name}</span>
                    <span className="font-medium text-red-600">₹{head.amount?.toLocaleString()}</span>
                  </div>
                ))}
                {loanData.excluded_heads?.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-4">सभी fees loan में शामिल हैं</p>
                )}
              </div>
            </div>
          </div>

          {/* Apply Button - Only for approved schools */}
          {isApproved ? (
            <button className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition">
              🎉 Apply for ₹{loanData.loan_calculation?.eligible_amount?.toLocaleString()} Loan
            </button>
          ) : (
            <div className="bg-yellow-50 border-2 border-yellow-300 border-dashed rounded-xl p-4 text-center">
              <span className="text-2xl">🔒</span>
              <p className="font-bold text-yellow-800 mt-2">Demo Mode</p>
              <p className="text-yellow-700 text-sm">
                NBFC approval के बाद Parents इस feature को access कर पाएंगे
              </p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

// Loan Application Form
const LoanApplicationForm = ({ schoolId, token, loanAmount, selectedClass, selectedTenure }) => {
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    parent_phone: '',
    aadhaar_last_4: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/padhai-emi/apply-with-fee-structure?school_id=${schoolId}&class_name=${selectedClass}&student_name=${formData.student_name}&parent_name=${formData.parent_name}&parent_phone=${formData.parent_phone}&tenure_months=${selectedTenure}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await res.json();
      if (data.success) {
        setResult(data);
      } else {
        alert(data.detail || 'Error submitting application');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error submitting application');
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="bg-green-50 rounded-xl p-8 text-center border border-green-200">
        <span className="text-5xl">🎉</span>
        <h3 className="text-xl font-bold text-green-700 mt-4">{result.message}</h3>
        <p className="text-green-600 mt-2">
          Application ID: <strong>{result.application?.application_id}</strong>
        </p>
        <div className="mt-4 text-left bg-white rounded-lg p-4">
          <h4 className="font-semibold mb-2">Next Steps:</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            {result.next_steps?.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
      <h3 className="font-bold text-lg mb-4">📝 Loan Application Form</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Student Name</label>
          <input
            type="text"
            required
            value={formData.student_name}
            onChange={(e) => setFormData({...formData, student_name: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            placeholder="विद्यार्थी का नाम"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Parent Name</label>
          <input
            type="text"
            required
            value={formData.parent_name}
            onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            placeholder="अभिभावक का नाम"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Mobile Number</label>
          <input
            type="tel"
            required
            value={formData.parent_phone}
            onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            placeholder="98765 43210"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Aadhaar Last 4 Digits</label>
          <input
            type="text"
            maxLength="4"
            value={formData.aadhaar_last_4}
            onChange={(e) => setFormData({...formData, aadhaar_last_4: e.target.value})}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            placeholder="XXXX"
          />
        </div>
      </div>

      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
        <div className="flex justify-between">
          <span>Loan Amount:</span>
          <span className="font-bold">₹{loanAmount?.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span>Class:</span>
          <span className="font-bold">{selectedClass}</span>
        </div>
        <div className="flex justify-between">
          <span>Tenure:</span>
          <span className="font-bold">{selectedTenure} months</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
      >
        {submitting ? '⏳ Submitting...' : '🚀 Submit Application'}
      </button>
    </form>
  );
};

// Main Page Component
export default function PadhaiEMIPage({ token, schoolId, userRole }) {
  const { language } = useLanguage();
  const [nbfcStatus, setNbfcStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calculator');

  useEffect(() => {
    checkNBFCStatus();
  }, [schoolId]);

  const checkNBFCStatus = async () => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/padhai-emi/school/${schoolId}/status`);
      const data = await res.json();
      if (data.success) {
        setNbfcStatus(data);
      }
    } catch (err) {
      console.error('Error checking NBFC status:', err);
    }
    setLoading(false);
  };

  const isApproved = nbfcStatus?.nbfc_approved || false;
  const isSchoolAdmin = userRole === 'school_admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // For parents of unapproved school - show nothing or redirect
  if (!isApproved && !isSchoolAdmin && userRole === 'parent') {
    return (
      <div className="p-6">
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <span className="text-5xl">🔒</span>
          <h2 className="text-xl font-bold text-gray-700 mt-4">Feature Not Available</h2>
          <p className="text-gray-500 mt-2">
            This feature is not enabled for your school yet.
            <br />Please contact school administration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="padhai-emi-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-yellow-500 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl">💰</span>
          <div>
            <h1 className="text-2xl font-bold">पढ़ाई EMI</h1>
            <p className="text-orange-100">School Fees अब आसान किस्तों में</p>
          </div>
          
          {/* Status Badge */}
          <span className={`ml-auto px-3 py-1 rounded-full text-sm font-bold ${
            isApproved 
              ? 'bg-green-400 text-green-900' 
              : 'bg-yellow-400 text-yellow-900'
          }`}>
            {isApproved ? '✅ NBFC Enabled' : '🔒 Demo Mode'}
          </span>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/20 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">0%</p>
            <p className="text-sm opacity-80">Processing Fee*</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">2 Min</p>
            <p className="text-sm opacity-80">Instant Approval</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">₹5L</p>
            <p className="text-sm opacity-80">Max Loan</p>
          </div>
        </div>
      </div>

      {/* Demo Mode Warning for School Admin */}
      {!isApproved && isSchoolAdmin && (
        <div className="bg-yellow-50 border-2 border-yellow-300 border-dashed rounded-xl p-4">
          <div className="flex items-center gap-4">
            <span className="text-3xl">🔒</span>
            <div>
              <h3 className="font-bold text-yellow-800">Demo Mode - Only You Can See This</h3>
              <p className="text-yellow-700 text-sm">
                Platform Admin से NBFC Approval मिलने के बाद ही Parents को यह feature दिखेगा।
                <br />
                अभी आप देख सकते हैं कि Parents को क्या दिखेगा।
              </p>
            </div>
          </div>
        </div>
      )}

      {/* NBFC Approved Banner */}
      {isApproved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-4">
          <span className="text-3xl">✅</span>
          <div>
            <h3 className="font-bold text-green-800">NBFC EMI Active</h3>
            <p className="text-green-700 text-sm">
              इस school के लिए EMI facility enabled है। Parents loan apply कर सकते हैं।
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: 'calculator', label: 'Fee Based Calculator', labelHi: 'फीस कैलकुलेटर', icon: '🧮' },
          { id: 'apply', label: 'Apply for Loan', labelHi: 'Loan Apply करें', icon: '📝', disabled: !isApproved },
          { id: 'status', label: 'My Applications', labelHi: 'मेरे Applications', icon: '📋' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'text-orange-500 border-b-2 border-orange-500' 
                : tab.disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {language === 'hi' ? tab.labelHi : tab.label}
            {tab.disabled && <span className="ml-1 text-xs">🔒</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'calculator' && (
        <FeeBasedLoanCalculator 
          schoolId={schoolId} 
          token={token}
          isApproved={isApproved}
        />
      )}

      {activeTab === 'apply' && isApproved && (
        <LoanApplicationForm 
          schoolId={schoolId}
          token={token}
          loanAmount={50000}
          selectedClass="Class 1"
          selectedTenure={6}
        />
      )}

      {activeTab === 'status' && (
        <div className="bg-white rounded-xl p-8 text-center shadow-sm border">
          <span className="text-4xl">📋</span>
          <h3 className="font-bold text-gray-700 mt-4">No Applications Yet</h3>
          <p className="text-gray-500 mt-2">आपने अभी तक कोई loan application नहीं किया है</p>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-bold text-blue-800 mb-4">🎯 How Padhai EMI Works</h3>
        <div className="grid grid-cols-4 gap-4">
          {[
            { step: 1, title: 'Select Class', desc: 'अपनी class choose करें' },
            { step: 2, title: 'Check Amount', desc: 'Eligible loan देखें' },
            { step: 3, title: 'Apply Online', desc: 'Form भरें, Aadhaar verify करें' },
            { step: 4, title: 'Get Approved', desc: 'NBFC approval, school को payment' }
          ].map(item => (
            <div key={item.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-2 font-bold">
                {item.step}
              </div>
              <h4 className="font-semibold text-blue-800">{item.title}</h4>
              <p className="text-xs text-blue-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div className="bg-gray-100 rounded-xl p-4 text-center text-sm text-gray-600">
        <p>
          ⚠️ <strong>Note:</strong> Loan amount is auto-calculated from school's official fee structure.
          Only official, non-refundable fees are covered by NBFC.
        </p>
      </div>
    </div>
  );
}
